export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
export type TransferState = 'draft' | 'saving' | 'waiting' | 'preparing' | 'uploading' | 'confirming' | 'processing' | 'done' | 'error' | 'local_error';
export type FileInfo = { name: string; type: string; size: number };
export type Transfer = {
  id: string; feature: string; type: string; owner: string; data: Json;
  title: string; group: string; href: string; files: FileInfo[];
  parentId?: string; state: TransferState; createdAt: string; updatedAt: string;
  progress: number | null; attempts: number; nextAttempt: number; error?: string;
  lease?: string; leaseUntil?: number; prepared?: boolean; result?: Json;
  receivedAt?: number; completedAt?: number; cancelRequested?: boolean;
  sendStarted?: boolean;
  // Locally discarded; only an optional server cancellation remains, without files.
  discardedAt?: number;
};
export type TransferInput = Pick<Transfer, 'type' | 'data' | 'title' | 'group' | 'href'> & {
  id?: string; files?: File[]; parentId?: string; draft?: boolean;
};
export type Receipt = { received: boolean; complete?: boolean; result?: Json; cancelled?: boolean };
export type TransferProgress = Pick<Transfer, 'id' | 'owner' | 'lease' | 'progress'> & { state: 'uploading' | 'confirming' };
export type TransferContext = {
  signal: AbortSignal; parent?: Transfer;
  // Fresh transport-safe wrappers around the persisted bytes. Use these for
  // uploads, not File objects read directly from IndexedDB; do not re-persist.
  files: () => Promise<File[]>;
  progress: (value: number | null, state?: 'uploading' | 'confirming') => void;
};
export type TransferAdapter = {
  // Both methods must use job.id as the server-side idempotency key and job.owner
  // as the expected authenticated owner. Never derive targets from the current page.
  // Only durable server acceptance is received:true; complete:false polls inspect
  // without uploading again. Files are released after durable acceptance.
  inspect: (job: Transfer, context: TransferContext) => Promise<Receipt>;
  send: (job: Transfer, context: TransferContext) => Promise<Receipt>;
  prepare?: (files: File[], job: Transfer) => Promise<File[]>;
  cancel?: (job: Transfer, context: TransferContext) => Promise<Receipt>;
};
export type AdapterLoader = () => Promise<{ default: Record<string, TransferAdapter> }>;
export type FileRecord = { id: string; original: File[]; prepared?: File[] };

export class TransferError extends Error {
  // An adapter may set this only when its endpoint guarantees zero side effects
  // for this rejection. It never clears an earlier uncertain send attempt.
  definitivelyRejected = false;
  constructor(message: string, public retryable = true, public authentication = false, public status?: number) { super(message); }
}
